import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import moment from 'moment';
import { useMemo } from 'react';
import * as React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AttachmentInput } from '~/components/AttachmentInput';
import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { AbsenceDecision } from '~/features/AbsenceChanges/AbsenceDecisionDialog/data';
import { useAbsenceDecisionDialog } from '~/features/AbsenceChanges/AbsenceDecisionDialog/useAbsenceDecisionDialog';
import { AbsencesTabContent_OnUpdateLegacy } from '~/library/AbsencesTabContent/component';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

export type AbsenceDecisionDialogProps = {
  employeeId: number;
  absenceId?: string;
  decision?: AbsenceDecision;
  onClose: () => void;
  open: boolean;
} & AbsencesTabContent_OnUpdateLegacy;

export const AbsenceDecisionDialog = (props: AbsenceDecisionDialogProps) => {
  const { open, onClose, decision, absenceId, employeeId, onUpdateLegacy } =
    props;

  const { t } = useTranslation();

  const { control, onSubmit, absenceEntry, isLoading, decisionRegistry } =
    useAbsenceDecisionDialog({
      decision,
      absenceId,
      employeeId,
      onClose,
      onUpdateLegacy,
    });

  const texts = useMemo(() => {
    switch (decision) {
      case AbsenceDecision.Approve:
        return {
          dialogTitle: 'laApproveAbsence',
          submitButtonText: 'laApprove',
        };
      case AbsenceDecision.Reject:
        return {
          dialogTitle: 'laRejectAbsence',
          submitButtonText: 'laReject',
        };
      case AbsenceDecision.RequestChanges:
        return {
          dialogTitle: 'laChangesRequested',
          submitButtonText: 'laChangesRequested',
        };
      default:
        return {
          dialogTitle: '',
          submitButtonText: '',
        };
    }
  }, [decision]);

  const { absenceSummary } = useMemo(() => {
    if (absenceEntry) {
      const { reason, startDate, endDate } = absenceEntry;

      return {
        absenceSummary: `${reason?.name}: ${moment(startDate).format(
          'L',
        )} - ${moment(endDate).format('L')}`,
      };
    }

    return {
      absenceSummary: '',
    };
  }, [absenceEntry]);

  if (isLoading && absenceEntry) {
    return <></>;
  }

  return (
    <DraggableDialog open={open} onClose={onClose}>
      <SolaForceDialogTitle onClose={onClose}>
        {t(texts.dialogTitle)}
      </SolaForceDialogTitle>
      <DialogContent>
        {isLoading && <LoadingView />}
        {!isLoading && !!absenceEntry && (
          <>
            <Typography mb={2}>{absenceSummary}</Typography>
            <Controller
              control={control}
              name={'info'}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    label={t('laInfo')}
                    {...field}
                    error={!!error}
                    helperText={error?.message}
                    fullWidth={true}
                  />
                );
              }}
            />
            {decisionRegistry.checkboxAvailable && (
              <Controller
                control={control}
                name={'addToDecisionRegistry'}
                render={({ field: { value, onChange } }) => {
                  return (
                    <FormControlLabel
                      label={t('laAddToDR')}
                      control={
                        <Checkbox
                          checked={value}
                          onChange={(event) => onChange(event.target.checked)}
                        />
                      }
                    />
                  );
                }}
              />
            )}
            {decisionRegistry.showSubForm && (
              <Box>
                <Controller
                  control={control}
                  name={'decisionRegistryEntry.decisionInfo'}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <TextField
                        label={t('laDecisionInfo')}
                        {...field}
                        error={!!error}
                        helperText={error?.message}
                        fullWidth={true}
                      />
                    );
                  }}
                />
                <Controller
                  control={control}
                  name={'decisionRegistryEntry.attachment'}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => {
                    return (
                      <AttachmentInput
                        onChange={(selectedFile?: File) => {
                          onChange(selectedFile || null);
                        }}
                        value={!!value ? value.name : ''}
                        attachmentUrl={undefined}
                        fullWidth={true}
                        margin={'normal'}
                        label={t('attachment')}
                        error={!!error?.message}
                        helperText={error?.message}
                      />
                    );
                  }}
                />
                <Controller
                  control={control}
                  name={'decisionRegistryEntry.reminderDate'}
                  render={({
                    field: { name, value, onChange, ref },
                    fieldState: { error },
                  }) => {
                    return (
                      <DatePicker
                        inputFormat="DD.MM.YYYY"
                        value={value}
                        onChange={(date) =>
                          onChange(!!date ? date.toDate() : null)
                        }
                        inputRef={ref}
                        minDate={moment().subtract(366, 'days')}
                        renderInput={(props) => (
                          <TextField
                            {...props}
                            name={name}
                            label={t('laReminder')}
                            fullWidth={true}
                            margin="normal"
                            error={!!error}
                            helperText={error?.message}
                          />
                        )}
                      />
                    );
                  }}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button onClick={onSubmit}>{t(texts.submitButtonText)}</Button>
      </DialogActions>
    </DraggableDialog>
  );
};
